import styles from './index.module.scss'
import { CommonMsgNodeProps } from '@/types/message'
import finishSvg from '@/assets/images/card-task-finish.png'
import loadingSvg from '@/assets/images/card-task-loading.svg'
import cn from 'classnames'
import { Image, Form, Input, Select, Row, Col, message } from 'antd'
import { CloseOutlined, FileImageOutlined } from '@ant-design/icons'
import LottieLoading from '@/components/lottie-loading'
import React from 'react'
import { CosUpload } from '@/components/upload'
import Button from '@/components/button'
import { geneCreativeDescAPI, type VideoProductImagesItem } from '@/proxy-request/api'
import useNodeSubmitDisabledStatus from '@/hooks/use-node-disabled'
import useMessageUpdateFlag from '@/hooks/use-message-update-flag'
import { observer } from 'mobx-react'
import { countryOptions } from '@/pages/main/sender-msg'
import SkeletonScreen from './skeleton-screen'
import { regeneImageAPI, reuploadImageAPI } from '@/servers/agent-v2'
import { saveActionRecordToServer } from '@/proxy-request/help'
import { safeParseJson } from '@/utils'
import { saveUserTextToChatPanel } from '@/proxy-request/chat-completion'

const MAX_PRODUCT_IMAGES = 9
const UPLOAD_MESSAGE_KEY = 'product-info-card-upload'
const REGEN_MESSAGE_KEY = 'product-info-card-regene'

interface ProductInfoCardProps extends CommonMsgNodeProps {
  node: {
    value: {
      text?: string
      category: string
      images: string[]
      has_face?: boolean[]
      product_name: string
      selling_point: string
      target_audience: string
      sales_region: string
      sales_language: string
      status?: number //1 生成中 2 生成完成
      disabled?: boolean
      ext_data?: string
    }
  }
}

/** 与 images 同长度、同下标；服务端缺项视为 false */
function syncHasFaceListToImagesLength(imagesLen: number, serverHasFace?: boolean[]): boolean[] {
  return Array.from({ length: imagesLen }, (_, i) => Boolean(serverHasFace?.[i]))
}

const normalizeImageUrls = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).slice(0, MAX_PRODUCT_IMAGES)
  }
  return value ? [value].slice(0, MAX_PRODUCT_IMAGES) : []
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getProductImagesFromValue = (value?: ProductInfoCardProps['node']['value']) => {
  const extData = value?.ext_data ? safeParseJson(value.ext_data || '') : null
  const historyData = isRecord(extData) ? extData : null
  const hasHistoryImages = historyData
    ? Object.prototype.hasOwnProperty.call(historyData, 'images')
    : false
  const historyImages = hasHistoryImages
    ? normalizeImageUrls(historyData?.images as string | string[] | undefined)
    : undefined
  const images = historyImages ?? normalizeImageUrls(value?.images)
  const historyHasFace = Array.isArray(historyData?.has_face)
    ? (historyData?.has_face as boolean[])
    : undefined
  const hasFaceList = syncHasFaceListToImagesLength(
    images.length,
    hasHistoryImages ? historyHasFace : value?.has_face
  )

  return {
    images,
    hasFaceList
  }
}

const getVideoProductImages = (
  images: string[],
  hasFaceList: boolean[]
): VideoProductImagesItem[] => {
  return images.map((imageUrl, index) => ({
    image_url: imageUrl,
    has_face: Boolean(hasFaceList[index])
  }))
}

const statusMap = {
  0: loadingSvg,
  1: loadingSvg,
  2: finishSvg,
  3: finishSvg
}

const ProductInfoCard = (props: ProductInfoCardProps) => {
  const { node } = props
  const { text = '', status = 2 } = node?.value || {}

  const [imageList, setImageList] = React.useState<string[]>(
    () => getProductImagesFromValue(props.node?.value).images
  )
  const [hasFaceList, setHasFaceList] = React.useState<boolean[]>(
    () => getProductImagesFromValue(props.node?.value).hasFaceList
  )
  const [deleteHoverIndex, setDeleteHoverIndex] = React.useState<number | null>(null)
  /** 上传回调会并发/连续触发，ref 用来在异步闭包里读取最新列表，避免 state 延迟导致重复处理 */
  const imageListRef = React.useRef<string[]>(imageList)
  const hasFaceListRef = React.useRef<boolean[]>(hasFaceList)
  /** 记录 CosUpload 上一次 onChange 的累计 URL 列表，用差集找出本次新增图片 */
  const uploadValueListRef = React.useRef<string[]>(imageList)
  /** 记录正在 reupload/检测人脸的 URL，防止连续回调时同一 URL 重复请求 */
  const pendingUploadUrlSetRef = React.useRef<Set<string>>(new Set())
  const [form] = Form.useForm()
  const [formValues, setFormValues] = React.useState<{
    category: string
    product_name: string
    selling_point: string
    target_audience: string
    sales_region: string | null
    sales_language: string | null
  }>({
    category: '',
    product_name: '',
    selling_point: '',
    target_audience: '',
    sales_region: null,
    sales_language: null
  })

  const { submitDisabled, setSubmitDisabled } = useNodeSubmitDisabledStatus({
    messageId: props?.msgContent?.message_id || '',
    initialDisabled: props?.node?.value?.disabled || false
  })

  /** 本地 hasFaceList 与 imageList 同下标；props.has_face 经 onChange 同步 */
  const hasFace = imageList.some((_, index) => Boolean(hasFaceList[index]))

  useMessageUpdateFlag(props.msgContent.message_id || '', {
    onChange: () => {
      const {
        category,
        product_name,
        selling_point,
        sales_region,
        sales_language,
        target_audience
      } = node?.value || {}

      const { images: nextImages, hasFaceList: nextHasFaceList } = getProductImagesFromValue(
        node?.value
      )
      setImageList(nextImages)
      imageListRef.current = nextImages
      uploadValueListRef.current = nextImages
      setHasFaceList(nextHasFaceList)
      hasFaceListRef.current = nextHasFaceList

      const data = {
        category,
        product_name,
        selling_point,
        target_audience,
        sales_region,
        sales_language
      }
      setFormValues(data)
      form.setFieldsValue(data)
    }
  })

  const updateFormValues = (values: {
    category?: string
    product_name?: string
    selling_point?: string
    target_audience?: string
    sales_region?: string
    sales_language?: string
  }) => {
    setFormValues(prev => ({
      ...prev,
      ...values
    }))
  }

  const saveProductImagesToHistory = React.useCallback(
    (images: string[], nextHasFaceList: boolean[]) => {
      const messageId = props?.msgContent?.message_id
      if (!messageId) {
        return
      }

      void saveActionRecordToServer({
        message_id: messageId,
        value: {
          ...node.value,
          images,
          has_face: Array.from({ length: images.length }, (_, index) =>
            Boolean(nextHasFaceList[index])
          )
        }
      }).catch(err => {
        console.error('save product images action record failed', err)
      })
    },
    [props?.msgContent?.message_id]
  )

  // status 为 0 或 1 时显示 loading 动画
  const isLoading = status === 0 || status === 1

  const onSubmit = async () => {
    if (imageList.length === 0) {
      message.error('请上传商品图片')
      return
    }
    if (!formValues.product_name) {
      message.error('请输入商品名称')
      return
    }
    if (!formValues.category) {
      message.error('请输入商品类目')
      return
    }

    if (!formValues.selling_point) {
      message.error('请输入商品卖点')
      return
    }
    if (!formValues.target_audience) {
      message.error('请输入目标受众')
      return
    }

    form.validateFields().then(() => {
      geneCreativeDescAPI({
        body: {
          message_id: props.msgContent.message_id,
          product_info: {
            category: formValues.category,
            product_name: formValues.product_name,
            selling_point: formValues.selling_point,
            target_audience: formValues.target_audience,
            sales_region: formValues.sales_region || ''
          },
          images: getVideoProductImages(imageList, hasFaceList)
        },
        opt: {
          onConnect() {
            setSubmitDisabled(true)
          },
          onBusinessError() {
            setSubmitDisabled(false)
          }
        }
      })
      saveUserTextToChatPanel('请帮我生成创意详情')
    })
  }

  const uploadImages = async (value: string | string[]) => {
    const nextUrls = Array.from(new Set(normalizeImageUrls(value)))
    /** CosUpload 多图模式会按 [A]、[A,B] 这类累计列表连续回调，这里只处理本次新增的 URL */
    const previousUploadUrlSet = new Set(uploadValueListRef.current)
    const uploadUrls = nextUrls.filter(
      url => !previousUploadUrlSet.has(url) && !pendingUploadUrlSetRef.current.has(url)
    )
    uploadValueListRef.current = nextUrls

    if (uploadUrls.length === 0) {
      return
    }

    const restCount =
      MAX_PRODUCT_IMAGES - imageListRef.current.length - pendingUploadUrlSetRef.current.size
    const urls = uploadUrls.slice(0, restCount)
    if (urls.length === 0) {
      message.warning(`最多上传 ${MAX_PRODUCT_IMAGES} 张商品图片`)
      return
    }

    urls.forEach(url => pendingUploadUrlSetRef.current.add(url))
    try {
      message.loading({ key: UPLOAD_MESSAGE_KEY, content: '上传中...', duration: 0 })

      /** 多张图片独立检测人脸，允许部分成功，所以用 allSettled 而不是 Promise.all */
      const results = await Promise.allSettled(
        urls.map(async imageUrl => {
          const res = await reuploadImageAPI({
            message_id: props?.msgContent?.message_id || '',
            image_url: imageUrl,
            origin_image_url: imageUrl
          })

          if (res?.code !== 0) {
            throw new Error(res?.message || '上传失败')
          }

          return {
            imageUrl,
            hasFace: Boolean(res?.data?.has_face)
          }
        })
      )

      const acceptedImages = results.flatMap(result =>
        result.status === 'fulfilled' ? [result.value] : []
      )

      const failedCount = results.length - acceptedImages.length
      const lastErrorMessage =
        [...results]
          .reverse()
          .find((result): result is PromiseRejectedResult => result.status === 'rejected')?.reason
          ?.message || ''

      message.destroy(UPLOAD_MESSAGE_KEY)
      if (acceptedImages.length > 0) {
        const nextImages = [...imageListRef.current]
        const nextHasFaceList = Array.from({ length: nextImages.length }, (_, index) =>
          Boolean(hasFaceListRef.current[index])
        )

        acceptedImages.forEach(({ imageUrl, hasFace: imageHasFace }) => {
          const existingIndex = nextImages.indexOf(imageUrl)
          if (existingIndex >= 0) {
            nextHasFaceList[existingIndex] = imageHasFace
            return
          }
          if (nextImages.length < MAX_PRODUCT_IMAGES) {
            nextImages.push(imageUrl)
            nextHasFaceList.push(imageHasFace)
          }
        })

        imageListRef.current = nextImages
        hasFaceListRef.current = nextHasFaceList
        setImageList(nextImages)
        setHasFaceList(nextHasFaceList)
        saveProductImagesToHistory(nextImages, nextHasFaceList)
        if (failedCount > 0) {
          message.warning(`部分图片上传失败，已成功上传 ${acceptedImages.length} 张`)
        } else {
          message.success('上传成功')
        }
      } else {
        message.error(lastErrorMessage || '上传失败')
      }
    } catch (err) {
      console.error('err', err)
      message.destroy(UPLOAD_MESSAGE_KEY)
      message.error('上传失败')
    } finally {
      urls.forEach(url => pendingUploadUrlSetRef.current.delete(url))
    }
  }

  const removeImage = (index: number) => {
    if (submitDisabled) {
      return
    }
    const nextImages = imageListRef.current.filter((_, itemIndex) => itemIndex !== index)
    const nextHasFaceList = hasFaceListRef.current.filter((_, itemIndex) => itemIndex !== index)
    imageListRef.current = nextImages
    uploadValueListRef.current = nextImages
    hasFaceListRef.current = nextHasFaceList
    setImageList(nextImages)
    setHasFaceList(nextHasFaceList)
    saveProductImagesToHistory(nextImages, nextHasFaceList)
    setDeleteHoverIndex(null)
  }

  /** 一键生成商品图：优先处理第一张含人脸图片，成功后用新 URL 替换列表项 */
  const handleRegeneProductImage = async () => {
    if (submitDisabled) {
      return
    }
    const faceImageIndex = hasFaceList.findIndex(Boolean)
    const targetImageIndex = faceImageIndex >= 0 ? faceImageIndex : 0
    const originImageUrl = imageList[targetImageIndex]
    if (!originImageUrl?.trim()) {
      message.error('暂无商品图片')
      return
    }
    message.loading({ key: REGEN_MESSAGE_KEY, content: '正在生成商品图...', duration: 0 })
    try {
      const res = await regeneImageAPI({
        image_url: originImageUrl
      })
      let errorMsg: string | null = null
      if (res?.code === 0) {
        const newImageUrl = res?.data?.image_url
        const reuploadRes = await reuploadImageAPI({
          message_id: props?.msgContent?.message_id || '',
          origin_image_url: originImageUrl,
          image_url: newImageUrl ?? ''
        })
        if (reuploadRes?.code === 0 && newImageUrl) {
          const nextImages = [...imageListRef.current]
          nextImages[targetImageIndex] = newImageUrl
          const nextHasFaceList = [...hasFaceListRef.current]
          while (nextHasFaceList.length <= targetImageIndex) {
            nextHasFaceList.push(false)
          }
          nextHasFaceList[targetImageIndex] = Boolean(reuploadRes?.data?.has_face)
          imageListRef.current = nextImages
          uploadValueListRef.current = nextImages
          hasFaceListRef.current = nextHasFaceList
          setImageList(nextImages)
          setHasFaceList(nextHasFaceList)
          saveProductImagesToHistory(nextImages, nextHasFaceList)
          message.success({ key: REGEN_MESSAGE_KEY, content: '一键生成商品图成功' })
        }
        if (reuploadRes?.message && reuploadRes.code !== 0) errorMsg = reuploadRes.message
      }
      if (res?.message && res.code !== 0) errorMsg = res.message
      if (errorMsg) message.error({ key: REGEN_MESSAGE_KEY, content: errorMsg })
    } catch (err) {
      console.error('err', err)
      message.error({ key: REGEN_MESSAGE_KEY, content: '生成失败' })
    }
  }

  if (status === 1 || status === 3) {
    return (
      <div>
        <SkeletonScreen status={status} title={status === 1 ? '商品信息生成中..' : text} />
      </div>
    )
  }
  const uploadDisabled = submitDisabled || imageList.length >= MAX_PRODUCT_IMAGES

  return (
    <div
      className={cn({
        [styles.loadingContainer]: isLoading,
        [styles.container]: !isLoading
      })}
    >
      <div className={styles.header}>
        {isLoading ? (
          <div className={styles.icon}>
            <LottieLoading width={20} height={20} />
          </div>
        ) : (
          <img
            className={cn(styles.icon, {
              [styles.loading]: isLoading
            })}
            src={statusMap[status]}
            alt="icon"
          />
        )}
        <span className={styles.title}>{'商品信息'}</span>
      </div>

      {!isLoading && (
        <>
          <div className={styles.productImageInfoBox}>
            <div className={styles.productImageInfoHeader}>
              <p className={styles.productImageLabel}>
                <span className={styles.required}>* </span>
                <span className={styles.labelMain}>商品图片</span>
              </p>
              <CosUpload
                useDraggerUpload={false}
                disabled={uploadDisabled}
                onChange={uploadImages}
                showDeleteBtn={false}
                multiple
                maxCount={MAX_PRODUCT_IMAGES}
                value={imageList}
                showSuccessMessage={false}
              >
                <span
                  className={cn(styles.uploadLink, {
                    [styles.uploadLinkDisabled]: uploadDisabled
                  })}
                >
                  上传图片
                </span>
              </CosUpload>
            </div>
            {imageList.length > 0 ? (
              <div className={styles.refImageList}>
                {imageList.map((image, index) => {
                  const deleteHovered = deleteHoverIndex === index
                  return (
                    <div
                      key={`${image}-${index}`}
                      className={cn(styles.refImageItem, {
                        [styles.refImageItemOverlayVisible]: deleteHovered
                      })}
                    >
                      <Image
                        src={image}
                        alt="商品图"
                        rootClassName={styles.refImageRoot}
                        preview={{
                          maskClosable: true,
                          mask: <span className={styles.previewMaskText}>预览</span>
                        }}
                      />
                      <span className={styles.refImageOverlayText}>
                        {deleteHovered ? '删除' : ''}
                      </span>
                      <button
                        type="button"
                        disabled={submitDisabled}
                        className={styles.deleteImageBtn}
                        onClick={e => {
                          e.stopPropagation()
                          removeImage(index)
                        }}
                        onMouseEnter={() => setDeleteHoverIndex(index)}
                        onMouseLeave={() => setDeleteHoverIndex(null)}
                        aria-label="删除图片"
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className={styles.refImagePlaceholder}>
                <FileImageOutlined className={styles.placeholderIcon} />
                <span>暂无商品图</span>
              </div>
            )}

            {hasFace ? (
              <div className={styles.warningRow}>
                <span className={styles.warningText}>
                  商品图中含有人脸可能会影响视频生成效果，请再上传一张新的商品图。或者试试
                </span>
                <div
                  className={cn(styles.warningLink, {
                    [styles.warningLinkDisabled]: submitDisabled
                  })}
                  onClick={handleRegeneProductImage}
                >
                  一键生成商品图
                </div>
              </div>
            ) : null}
          </div>

          <div className={styles.productFormBox}>
            <Form
              form={form}
              disabled={submitDisabled}
              onValuesChange={(_, values) => updateFormValues(values)}
              layout="vertical"
              className={styles.productForm}
              initialValues={formValues}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    rules={[{ required: true, message: '请输入商品名称' }]}
                    name="product_name"
                    label="商品名称:"
                  >
                    <Input placeholder="请输入商品名称" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    rules={[{ required: true, message: '请输入商品类目' }]}
                    name="category"
                    label="商品类目:"
                  >
                    <Input placeholder="请输入商品类目" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                rules={[{ required: true, message: '请输入商品卖点' }]}
                name="selling_point"
                label="商品卖点:"
              >
                <Input.TextArea rows={3} placeholder="请输入商品卖点" />
              </Form.Item>
              <Form.Item
                rules={[{ required: true, message: '请输入目标受众' }]}
                name="target_audience"
                label="目标受众:"
              >
                <Input.TextArea rows={3} placeholder="请输入目标受众" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="sales_region" label="销售地区:">
                    <Select placeholder="请选择销售地区" options={countryOptions} />
                  </Form.Item>
                </Col>
                {/* <Col span={12}>
                  <Form.Item name="sales_language" label="销售语言">
                    <Select
                      placeholder="请选择销售语言"
                      options={[
                        { value: 'en', label: '英语' },
                        { value: 'ja', label: '日语' },
                        { value: 'zh-CN', label: '中文' }
                      ]}
                    />
                  </Form.Item>
                </Col> */}
              </Row>
            </Form>
          </div>

          <Button onClick={onSubmit} disabled={submitDisabled}>
            <span>生成创意详情</span>
          </Button>
        </>
      )}
    </div>
  )
}

export default observer(ProductInfoCard)
